import {
  isLOA3,
  isLoggedIn,
  isProfileLoading,
  selectProfile,
} from '@department-of-veterans-affairs/platform-user/selectors';
import { focusElement } from '@department-of-veterans-affairs/platform-utilities/ui';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { toggleLoginModal as toggleLoginModalAction } from '~/platform/site-wide/user-nav/actions';
import { getStartPageLink } from '../utils/links';
import {
  getUnauthenticatedStartLink,
  UnauthenticatedIntro,
} from '../components/introduction/UnauthenticatedUi';
import { AuthenticatedIntro } from '../components/introduction/AuthenticatedUi';
import StatusChecker from '../components/introduction/StatusChecker';
import { topQuestions } from '../components/introduction/top-questions';

const IntroductionPage = ({
  route,
  showSignInModal,
  userIsLoggedIn,
  showLoadingIndicator,
  isUserLOA3,
  signInServiceName,
}) => {
  const { formConfig, pageList, pathname, formData } = route;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('showSignInModal') === 'true' &&
      !userIsLoggedIn &&
      !showLoadingIndicator
    ) {
      showSignInModal();
    }
  }, [userIsLoggedIn, showLoadingIndicator, showSignInModal]);

  useEffect(() => {
    focusElement('.schemaform-title > h1');
  }, []);

  if (showLoadingIndicator) {
    return <va-loading-indicator set-focus />;
  }

  const showAuthenticatedUi = userIsLoggedIn && isUserLOA3;

  return (
    <div className="schemaform-intro">
      <div className="schemaform-title vads-u-margin-bottom--2">
        <h1 className="vads-u-margin-bottom--2p5" data-testid="form-title">
          {formConfig.title}
        </h1>
        <div className="schemaform-subtitle" data-testid="form-subtitle">
          Get answers to your questions about VA benefits and services. You
          should receive a reply within 7 business days.
        </div>
      </div>
      <div className="vads-u-margin-top--4">
        <va-details label="When to use Ask VA or get urgent support">
          <p className="vads-u-margin-top--0">
            You can only use Ask VA for non-urgent needs. If you need help now,
            use one of these urgent communication options:
          </p>
          <ul>
            <li>
              <strong>If you’re in crisis or having thoughts of suicide</strong>
              , connect with a Veterans Crisis Line responder anytime, day or
              night, in these 3 ways:
              <ul>
                <li>
                  Call <va-telephone contact="988" />, then select 1.
                </li>
                <li>
                  Start a{' '}
                  <va-link
                    rel="noreferrer noopener"
                    href="https://www.veteranscrisisline.net/get-help/chat"
                    text="confidential chat"
                    external
                  />
                  .
                </li>
                <li>
                  Text <va-telephone contact="838255" />.
                </li>
              </ul>
            </li>
            <li>
              <strong>If you think your life or health is in danger</strong>,
              call{' '}
              <va-telephone contact="911" message-aria-describedby="9 1 1" />,
              or go to the nearest emergency room.
            </li>
          </ul>
        </va-details>
      </div>
      {showAuthenticatedUi ? (
        <AuthenticatedIntro
          formStartLink={getStartPageLink(pathname, pageList, formData)}
        />
      ) : (
        <UnauthenticatedIntro {...{ showSignInModal, signInServiceName }} />
      )}
      <div>
        <h3 className="vads-u-margin-top--4 vads-u-margin-bottom--0">
          If you need general information
        </h3>
        <p>
          You can explore our FAQs and other resources to get information about
          VA benefits and services or you can ask your question to our chatbot.
        </p>
        <p>
          <va-link
            href="https://www.va.gov/resources/"
            text="Explore our resources and support content"
          />
        </p>
        <p>
          <va-link
            href="https://www.va.gov/contact-us/virtual-agent/"
            text="Learn more about the VA chatbot"
          />
        </p>
      </div>
      {!showAuthenticatedUi &&
        getUnauthenticatedStartLink(
          getStartPageLink(pathname, pageList, formData),
        )}
      {topQuestions}
      {!showAuthenticatedUi && <StatusChecker />}
    </div>
  );
};

IntroductionPage.propTypes = {
  showSignInModal: PropTypes.func.isRequired,
  isUserLOA3: PropTypes.bool,
  route: PropTypes.shape({
    formConfig: PropTypes.shape({
      formId: PropTypes.string,
      title: PropTypes.string,
      subTitle: PropTypes.string,
      prefillEnabled: PropTypes.bool,
      savedFormMessages: PropTypes.shape({}),
      downtime: PropTypes.shape({}),
    }),
    formData: PropTypes.object,
    pathname: PropTypes.string,
    pageList: PropTypes.array,
  }),
  showLoadingIndicator: PropTypes.bool,
  signInServiceName: PropTypes.string,
  userIsLoggedIn: PropTypes.bool,
};

function mapStateToProps(state) {
  const profileIsLoading = isProfileLoading(state);
  const userIsLoggedIn = isLoggedIn(state);
  return {
    userIsLoggedIn,
    showLoadingIndicator: profileIsLoading,
    isUserLOA3: !profileIsLoading && userIsLoggedIn && isLOA3(state),
    signInServiceName: selectProfile(state).signIn?.serviceName,
  };
}

const mapDispatchToProps = dispatch => ({
  showSignInModal: () => dispatch(toggleLoginModalAction(true, 'ask-va', true)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(IntroductionPage));
